import type { Rol } from '@app/shared';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useCurrentUser } from '../features/auth/hooks/useCurrentUser';
import { useI18n } from '../i18n/I18nProvider';

interface RequireAuthProps {
  children: ReactNode;
  /** Si se especifica, solo deja pasar usuarios con uno de estos roles. */
  roles?: Rol[];
}

// Guard de rutas. Mientras carga, muestra loader; si no hay auth, redirige a /login
// con ?next= preservando la ruta original. Si hay rol no permitido, muestra 403 simple.
export function RequireAuth({ children, roles }: RequireAuthProps): JSX.Element {
  const { data: user, isLoading, error } = useCurrentUser();
  const location = useLocation();
  const es = useI18n();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-marca-gris/60">
        {es.app.cargando}
      </div>
    );
  }

  if (error || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Si el usuario debe cambiar password, forzar redirección excepto si ya está ahí.
  if (user.debeCambiarPassword && location.pathname !== '/cambiar-password') {
    return <Navigate to="/cambiar-password" replace />;
  }

  if (roles && !roles.includes(user.rol)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="font-titulos text-3xl text-danger">{es.errores.auth.sinPermiso}</h1>
        <p className="text-sm text-marca-gris/70">{es.errores.auth.sinPermisoDetalle}</p>
      </div>
    );
  }

  return <>{children}</>;
}
